import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import { BRAND_ORANGE } from '../../../../styles/portalTheme';
import {
  canArchivePartnerEvent,
  isPartnerEventArchived,
  shouldHidePartnerEventTimeline,
} from '../partnerEventArchive';
import { isAttendanceFinalized } from '../attendanceFinalizeUtils';
import EventModerationTimeline, { getLatestModerationStatus } from '../eventModerationTimeline';

const SidebarCard = ({ title, children }) => (
  <Box
    sx={{
      backgroundColor: 'background.paper',
      borderRadius: 2.5,
      p: 2,
      boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.06)',
    }}
  >
    <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', color: 'text.primary', mb: 1.75 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const ManagementButton = ({ icon, children, onClick, variant = 'outlined', disabled = false }) => (
  <Button
    fullWidth
    variant={variant}
    disabled={disabled}
    onClick={onClick}
    startIcon={icon}
    sx={{
      mb: 1.25,
      py: 1.05,
      textTransform: 'none',
      fontWeight: 700,
      borderRadius: 1.5,
      ...(variant === 'contained'
        ? {
            bgcolor: BRAND_ORANGE,
            boxShadow: 'none',
            '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
          }
        : {
            color: BRAND_ORANGE,
            borderColor: '#e6b48f',
            '&:hover': {
              borderColor: BRAND_ORANGE,
              backgroundColor: 'rgba(224, 104, 10, 0.04)',
            },
          }),
    }}
  >
    {children}
  </Button>
);

const PartnerEventDetailsSidebar = ({
  eventData,
  canSubmit,
  canFinalizeAttendance,
  canViewAttendance = false,
  showEdit,
  submitting,
  onSubmit,
  onFinalizeAttendance,
  onOpenModerationFeedback,
  onEdit,
  onDuplicate,
  onArchive,
}) => {
  const latestModerationStatus = useMemo(() => getLatestModerationStatus(eventData), [eventData]);
  const canResubmit = latestModerationStatus === 'requested';
  const archived = isPartnerEventArchived(eventData);
  const attendanceFinalized = isAttendanceFinalized(eventData);
  const showArchive = canArchivePartnerEvent(eventData) && Boolean(onArchive);
  const showTimeline = !shouldHidePartnerEventTimeline(eventData);
  const attendanceActionEnabled = canSubmit || canFinalizeAttendance || canViewAttendance;
  const attendanceActionLabel = canSubmit
    ? 'Submit for Review'
    : attendanceFinalized
      ? 'View Attendance'
      : 'Finalize Attendance';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      {!archived ? (
        <SidebarCard title="Event Management">
          <ManagementButton
            icon={<TaskAltOutlinedIcon />}
            variant="contained"
            disabled={submitting || !attendanceActionEnabled}
            onClick={canSubmit ? onSubmit : onFinalizeAttendance}
          >
            {attendanceActionLabel}
          </ManagementButton>
          {canResubmit ? (
            <ManagementButton
              icon={<TaskAltOutlinedIcon />}
              variant="contained"
              disabled={submitting}
              onClick={onOpenModerationFeedback}
            >
              Re-Submit Event
            </ManagementButton>
          ) : null}
          {showEdit ? (
            <ManagementButton icon={<EditOutlinedIcon />} onClick={onEdit}>
              Edit Event
            </ManagementButton>
          ) : null}
          {onDuplicate ? (
            <ManagementButton icon={<ContentCopyOutlinedIcon />} onClick={onDuplicate}>
              Duplicate Event
            </ManagementButton>
          ) : null}
          {showArchive ? (
            <ManagementButton icon={<ArchiveOutlinedIcon />} onClick={onArchive}>
              Archive Event
            </ManagementButton>
          ) : null}
        </SidebarCard>
      ) : null}

      {showTimeline ? (
        <EventModerationTimeline event={eventData} title="Status Timeline" showCompletedTimeline />
      ) : null}
    </Box>
  );
};

export default PartnerEventDetailsSidebar;
